// src/components/Dashboard.tsx
import React, { useState, useEffect } from "react";
import { 
  FiActivity, FiBriefcase, FiFolder, FiClock,
  FiCheck, FiTarget, FiAlertCircle
} from "react-icons/fi";
import { supabase } from "../lib/supabase";
import GlobalSidebar from "./layout/GlobalSidebar";
import GlobalHeader from "./layout/GlobalHeader";
import TaskEditModal from "./modals/TaskEditModal";
import GoogleCalendarSetup from "./GoogleCalendarSetup";
import { isGoogleCalendarConfigured } from "../services/googleCalendar";
import type { Task, Project, Company } from "../types/domain";
import "./Dashboard.css";

interface DashboardStats {
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  totalProjects: number;
  activeProjects: number;
  totalCompanies: number;
  tasksByStatus: Record<string, number>;
  tasksByPriority: Record<string, number>;
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    overdueTasks: 0,
    totalProjects: 0,
    activeProjects: 0,
    totalCompanies: 0,
    tasksByStatus: {},
    tasksByPriority: {}
  });
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  // Cargar datos del dashboard
  useEffect(() => {
    document.title = "TaskManager - Inicio";
    
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Cargar tareas
          const { data: allTasks } = await supabase
            .from("tasks")
            .select("*")
            .order('created_at', { ascending: false })
            .limit(1000);

          // Cargar proyectos
          const { data: allProjects } = await supabase
            .from("projects")
            .select("*")
            .order('name');

          // Cargar empresas
          const { data: allCompanies } = await supabase
            .from("companies")
            .select("*")
            .order('name');

          if (allTasks) setTasks(allTasks as Task[]);
          if (allProjects) setProjects(allProjects as Project[]);
          if (allCompanies) setCompanies(allCompanies as Company[]);

          // Calcular estadísticas
          calculateStats(allTasks || [], allProjects || [], allCompanies || []);
          
          // Tareas recientes (últimas 5)
          setRecentTasks((allTasks || []).slice(0, 5) as Task[]);
        }
      } catch (error) {
        console.error("Error cargando datos del dashboard:", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const reloadData = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Cargar tareas
        const { data: allTasks } = await supabase
          .from("tasks")
          .select("*")
          .order('created_at', { ascending: false })
          .limit(1000);

        // Cargar proyectos
        const { data: allProjects } = await supabase
          .from("projects")
          .select("*")
          .order('name');

        // Cargar empresas
        const { data: allCompanies } = await supabase
          .from("companies")
          .select("*")
          .order('name');

        if (allTasks) setTasks(allTasks as Task[]);
        if (allProjects) setProjects(allProjects as Project[]);
        if (allCompanies) setCompanies(allCompanies as Company[]);

        // Calcular estadísticas
        calculateStats(allTasks || [], allProjects || [], allCompanies || []);
        
        // Tareas recientes (últimas 5)
        setRecentTasks((allTasks || []).slice(0, 5) as Task[]);
      }
    } catch (error) {
      console.error("Error recargando datos del dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (allTasks: Task[], allProjects: Project[], allCompanies: Company[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const completedTasks = allTasks.filter(t => t.status === 'done').length;
    const pendingTasks = allTasks.filter(t => t.status !== 'done' && t.status !== 'archived').length;
    const overdueTasks = allTasks.filter(t => {
      if (!t.deadline || t.status === 'done' || t.status === 'archived') return false;
      const deadline = new Date(t.deadline);
      deadline.setHours(0, 0, 0, 0);
      return deadline < today;
    }).length;

    const activeProjects = allProjects.filter(p => {
      return allTasks.some(t => t.project_id === p.id && t.status !== 'archived');
    }).length;

    const tasksByStatus = allTasks.reduce((acc, task) => {
      acc[task.status] = (acc[task.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const tasksByPriority = allTasks.reduce((acc, task) => {
      const priority = task.priority || 'none';
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    setStats({
      totalTasks: allTasks.length,
      completedTasks,
      pendingTasks,
      overdueTasks,
      totalProjects: allProjects.length,
      activeProjects,
      totalCompanies: allCompanies.length,
      tasksByStatus,
      tasksByPriority
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case 'urgent': return 'var(--priority-urgent)';
      case 'high': return 'var(--priority-high)';
      case 'medium': return 'var(--priority-medium)';
      case 'low': return 'var(--priority-low)';
      default: return 'var(--jira-text-subtle)';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'var(--status-done)';
      case 'in_progress': return 'var(--status-progress)';
      case 'in_review': return 'var(--status-review)';
      case 'todo': return 'var(--status-todo)';
      case 'backlog': return 'var(--status-backlog)';
      default: return 'var(--jira-text-subtle)';
    }
  };

  return (
    <>
      <GlobalHeader
        viewType="dashboard"
        onRefreshClick={reloadData}
        onCreateTask={() => {
          setShowCreateTaskModal(true);
        }}
        onUserClick={() => {}}
      />
      
      <GlobalSidebar
        projects={projects}
        companies={companies}
        tasks={tasks}
        showFilters={false}
        showProjectStats={false}
        onProjectChange={() => {}}
        onCompanyChange={() => {}}
        onPriorityChange={() => {}}
        onAssigneeChange={() => {}}
        onSearchChange={() => {}}
        onClearFilters={() => {}}
      />

      <div className="dashboard-layout">
        <div className="dashboard-content">
          <div className="dashboard-header">
            <h1>Panel de Control</h1>
            <p>Resumen general de tus proyectos y tareas</p>
          </div>

          {loading ? (
            <div className="loading-state">
              <FiActivity size={24} />
              <span>Cargando datos...</span>
            </div>
          ) : (
            <>
              {/* Tarjetas de métricas principales */}
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-header">
                    <FiTarget size={20} />
                    <span>Total Tareas</span>
                  </div>
                  <div className="metric-value">{stats.totalTasks}</div>
                </div>

                <div className="metric-card">
                  <div className="metric-header">
                    <FiCheck size={20} />
                    <span>Completadas</span>
                  </div>
                  <div className="metric-value completed">{stats.completedTasks}</div>
                  <div className="metric-subtitle">
                    {stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}% del total
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-header">
                    <FiClock size={20} />
                    <span>Pendientes</span>
                  </div>
                  <div className="metric-value pending">{stats.pendingTasks}</div>
                </div>

                <div className="metric-card">
                  <div className="metric-header">
                    <FiAlertCircle size={20} />
                    <span>Vencidas</span>
                  </div>
                  <div className="metric-value overdue">{stats.overdueTasks}</div>
                </div>

                <div className="metric-card">
                  <div className="metric-header">
                    <FiFolder size={20} />
                    <span>Proyectos</span>
                  </div>
                  <div className="metric-value">{stats.totalProjects}</div>
                  <div className="metric-subtitle">{stats.activeProjects} activos</div>
                </div>

                <div className="metric-card">
                  <div className="metric-header">
                    <FiBriefcase size={20} />
                    <span>Empresas</span>
                  </div>
                  <div className="metric-value">{stats.totalCompanies}</div>
                </div>
              </div>

              {/* Configuración de Google Calendar */}
              {!isGoogleCalendarConfigured() && (
                <div style={{ marginBottom: 24 }}>
                  <GoogleCalendarSetup />
                </div>
              )}

              {/* Gráficos y datos adicionales */}
              <div className="dashboard-grid">
                {/* Tareas por estado */}
                <div className="dashboard-section">
                  <h3>Tareas por Estado</h3>
                  <div className="status-chart">
                    {Object.entries(stats.tasksByStatus).map(([status, count]) => (
                      <div key={status} className="status-bar">
                        <div className="status-label">
                          <span className="status-name">{status}</span>
                          <span className="status-count">{count}</span>
                        </div>
                        <div className="status-progress">
                          <div 
                            className="status-fill" 
                            style={{ 
                              width: `${(count / stats.totalTasks) * 100}%`,
                              backgroundColor: getStatusColor(status)
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tareas por prioridad */}
                <div className="dashboard-section">
                  <h3>Tareas por Prioridad</h3>
                  <div className="priority-chart">
                    {Object.entries(stats.tasksByPriority).map(([priority, count]) => (
                      <div key={priority} className="priority-bar">
                        <div className="priority-label">
                          <span className="priority-name">{priority === 'none' ? 'Sin prioridad' : priority}</span>
                          <span className="priority-count">{count}</span>
                        </div>
                        <div className="priority-progress">
                          <div 
                            className="priority-fill" 
                            style={{ 
                              width: `${(count / stats.totalTasks) * 100}%`,
                              backgroundColor: getPriorityColor(priority === 'none' ? null : priority)
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tareas recientes */}
                <div className="dashboard-section recent-tasks">
                  <h3>Tareas Recientes</h3>
                  <div className="recent-tasks-list">
                    {recentTasks.map((task) => (
                      <div key={task.id} className="recent-task">
                        <div className="task-info">
                          <span className="task-title">{task.title}</span>
                          <span className="task-date">
                            {formatDate(task.created_at)}
                          </span>
                        </div>
                        <div className="task-badges">
                          <span 
                            className="task-status" 
                            style={{ color: getStatusColor(task.status) }}
                          >
                            {task.status}
                          </span>
                          {task.priority && (
                            <span 
                              className="task-priority"
                              style={{ color: getPriorityColor(task.priority) }}
                            >
                              {task.priority}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de crear tarea */}
      <TaskEditModal
        task={null}
        isOpen={showCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
        onSave={async (newTask: Task) => {
          await reloadData();
          setShowCreateTaskModal(false);
        }}
        projects={projects}
        showStatusField={true}
        showDateField={true}
      />
    </>
  );
}